import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { initFlexxSync, flushFlexxSync, teardownFlexxSync, clearLocalFlexxState } from "@/flexx/sync";
import { startCourseXPBridge, stopCourseXPBridge, resetCourseXPBridgeBaseline } from "@/flexx/courseXPBridge";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Auth storage (XSS-hardened) ─────────────────────────────────────────
// The real JWT lives ONLY in the httpOnly `access_token` / `refresh_token`
// cookies (set by the backend, unreadable from JS). localStorage holds only
// a NON-SENSITIVE sentinel so the many `if (!token) return` "am I logged in?"
// gates across the app keep working without exposing a stealable token.
const AUTH_SENTINEL = "cookie-session";
const markAuthed = () => {
  try {
    localStorage.setItem("flexx_access_token", AUTH_SENTINEL);
    localStorage.removeItem("flexx_refresh_token"); // purge any legacy real token
  } catch (_) {}
};
const clearAuthed = () => {
  try {
    localStorage.removeItem("flexx_access_token");
    localStorage.removeItem("flexx_refresh_token");
  } catch (_) {}
};

// Cookies authenticate every request — just send credentials.
const authAxios = axios.create({ withCredentials: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`${API}/auth/me`);
      markAuthed();
      await initFlexxSync();
      startCourseXPBridge();
      setUser(data);
    } catch {
      // Access cookie missing/expired — try a cookie-based refresh (the
      // refresh_token httpOnly cookie is sent automatically).
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const { data } = await authAxios.get(`${API}/auth/me`);
        markAuthed();
        await initFlexxSync();
        startCourseXPBridge();
        setUser(data);
      } catch {
        clearAuthed();
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const formatErr = (d) => typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e.msg).join(", ") : "Something went wrong.";

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      markAuthed();
      await initFlexxSync();
      resetCourseXPBridgeBaseline();
      startCourseXPBridge();
      // Pull the full profile from /me — keeps new flags (pmir_access,
      // intel_unlocked, axel_quant_beta_enabled, …) automatically without
      // a brittle field whitelist here.
      try {
        const { data: me } = await authAxios.get(`${API}/auth/me`);
        setUser(me);
      } catch {
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role, course_access: data.course_access, is_owner: data.is_owner, admin_permissions: data.admin_permissions || [] });
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatErr(e.response?.data?.detail) };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
      markAuthed();
      await initFlexxSync();
      resetCourseXPBridgeBaseline();
      startCourseXPBridge();
      try {
        const { data: me } = await authAxios.get(`${API}/auth/me`);
        setUser(me);
      } catch {
        setUser({ id: data.id, email: data.email, name: data.name, role: data.role, course_access: data.course_access, is_owner: data.is_owner, admin_permissions: data.admin_permissions || [] });
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatErr(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await flushFlexxSync(); } catch {}
    try { await authAxios.post(`${API}/auth/logout`); } catch {}
    stopCourseXPBridge();
    teardownFlexxSync();
    clearLocalFlexxState();
    clearAuthed();
    setUser(false);
  };

  // Active Sessions (single-session anti-sharing model)
  const listSessions = async () => {
    const { data } = await authAxios.get(`${API}/auth/sessions`);
    return { sessions: data.sessions || [], multiSession: !!data.multi_session };
  };

  const signOutAllDevices = async () => {
    try { await flushFlexxSync(); } catch {}
    try { await authAxios.post(`${API}/auth/sessions/revoke-all`); } catch {}
    stopCourseXPBridge();
    teardownFlexxSync();
    clearLocalFlexxState();
    clearAuthed();
    setUser(false);
    return { success: true };
  };

  // If this session gets superseded by a login on another device, any 401
  // with that detail (dispatched from index.js) logs us out with a notice.
  useEffect(() => {
    const onSuperseded = () => {
      try { sessionStorage.setItem("flexx_session_superseded", "1"); } catch (_) {}
      stopCourseXPBridge();
      teardownFlexxSync();
      clearLocalFlexxState();
      clearAuthed();
      setUser(false);
    };
    window.addEventListener("flexx:session-superseded", onSuperseded);
    return () => window.removeEventListener("flexx:session-superseded", onSuperseded);
  }, []);

  const hasAccess = (courseId) => user && (user.course_access || []).includes(courseId);

  if (loading) return null;
  return (
    <AuthContext.Provider value={{ user, login, register, logout, hasAccess, setUser, authAxios, listSessions, signOutAllDevices }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
